import type { Views } from '@biopeak/db';
import { useQuery } from '@tanstack/react-query';
import { supabase } from './supabase.ts';

/**
 * Painel SERBIO — protocolo do Dr. Serfaty (06/08/2026).
 *
 * NENHUM CÁLCULO ACONTECE AQUI, e isso é a regra mais importante deste
 * arquivo. Índice, corte, classificação e gatilho vêm resolvidos das views
 * `serbio_panel` e `serbio_indices`. Reproduzir qualquer corte em TypeScript
 * criaria uma segunda régua clínica que ninguém lembraria de atualizar junto
 * — e a que ficasse para trás continuaria pintando a tela com confiança.
 */

export type SerbioColeta = Views<'serbio_panel'>;
export type SerbioIndice = Views<'serbio_indices'>;

/** Uma linha por coleta, da mais recente para a mais antiga. */
export function useSerbioColetas(patientId: string) {
  return useQuery({
    queryKey: ['serbio-coletas', patientId],
    queryFn: async (): Promise<SerbioColeta[]> => {
      const { data, error } = await supabase
        .from('serbio_panel')
        .select('*')
        .eq('patient_id', patientId)
        .order('data_coleta', { ascending: false });

      if (error) throw error;
      return data ?? [];
    },
  });
}

/** Todos os índices de todas as coletas — a tela filtra pela data escolhida. */
export function useSerbioIndices(patientId: string) {
  return useQuery({
    queryKey: ['serbio-indices', patientId],
    queryFn: async (): Promise<SerbioIndice[]> => {
      const { data, error } = await supabase
        .from('serbio_indices')
        .select('*')
        .eq('patient_id', patientId)
        .order('ordem', { ascending: true });

      if (error) throw error;
      return data ?? [];
    },
  });
}

/** Ordem de exibição dos domínios, fixa — a do protocolo, não alfabética. */
export const DOMINIOS = [
  'Inflamatório',
  'Metabólico',
  'Lipídico',
  'Hepático',
  'Vascular',
] as const;

export function formatarData(iso: string | null): string {
  if (!iso) return '—';
  const [ano, mes, dia] = iso.split('-');
  return `${dia}/${mes}/${ano}`;
}
